export function minutesToHhMm(minutes?: number | null): string {
  if (minutes == null || minutes < 0) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

export function formatMinutesForDisplay(minutes?: number | null): string | null {
  const formatted = minutesToHhMm(minutes);
  return formatted !== '' ? formatted : null;
}

export function decimalHoursToHhMm(hours?: number | string | null): string {
  if (hours == null || hours === '') return '';
  const totalMinutes = Math.round(Number(hours) * 60);
  return minutesToHhMm(totalMinutes);
}

export function formatCourseDate(value?: string | Date | null): string | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Segundos totales del curso (duración = 100%). */
export function courseTotalActiveSeconds(academicHours?: number | string | null): number | null {
  if (academicHours == null || academicHours === '') return null;
  const hours = Number(academicHours);
  if (Number.isNaN(hours) || hours <= 0) return null;
  return Math.round(hours * 3600);
}

/** Segundos activos mínimos = duración del curso × porcentaje mínimo. */
export function courseMinActiveSeconds(
  academicHours?: number | string | null,
  minCompliancePercent?: number | string | null,
): number | null {
  if (academicHours == null || academicHours === '' || minCompliancePercent == null || minCompliancePercent === '') {
    return null;
  }
  const hours = Number(academicHours);
  const percent = Number(minCompliancePercent);
  if (Number.isNaN(hours) || hours <= 0 || Number.isNaN(percent) || percent <= 0) return null;
  return Math.round(hours * 3600 * (percent / 100));
}

export function courseProgressPercent(activeSeconds: number, totalSeconds: number): number {
  if (totalSeconds <= 0) return 0;
  const raw = (activeSeconds / totalSeconds) * 100;
  return Math.min(100, Math.round(raw * 10) / 10);
}

export function formatCourseProgressPercent(activeSeconds: number, totalSeconds: number): string {
  const value = courseProgressPercent(activeSeconds, totalSeconds);
  if (value > 0 && value < 0.1) return '<0,1%';
  return `${value.toLocaleString('es-CL', { maximumFractionDigits: 1 })}%`;
}

export function meetsCourseMinCompliance(
  activeSeconds: number,
  academicHours?: number | string | null,
  minCompliancePercent?: number | string | null,
): boolean | null {
  const required = courseMinActiveSeconds(academicHours, minCompliancePercent);
  if (required == null) return null;
  return activeSeconds >= required;
}

export function formatCompliancePercent(value?: number | string | null): string | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return null;
  return `${parsed.toLocaleString('es-CL', { maximumFractionDigits: 2 })}%`;
}

/** Acepta horas enteras (150) o HH:MM (150:00). */
export function parseCourseDurationHours(value?: string | null): number | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  const hhMm = /^(\d{1,3}):(\d{2})$/.exec(trimmed);
  if (hhMm) {
    const hours = Number.parseInt(hhMm[1], 10);
    const mins = Number.parseInt(hhMm[2], 10);
    if (mins > 59) return null;
    const total = hours * 60 + mins;
    if (total <= 0) return null;
    return Math.round((total / 60) * 100) / 100;
  }
  const asNumber = Number.parseFloat(trimmed.replace(',', '.'));
  if (Number.isNaN(asNumber) || asNumber <= 0) return null;
  return Math.round(asNumber * 100) / 100;
}

export function formatCourseDurationHours(hours?: number | string | null): string {
  if (hours == null || hours === '') return '';
  const parsed = Number(hours);
  if (Number.isNaN(parsed)) return '';
  if (Number.isInteger(parsed)) return String(parsed);
  return String(parsed);
}
